import { Component, OnInit, OnDestroy, EventEmitter, Output, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { finalize, Subject, Subscription, takeUntil } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../../../global/services/global.service';
import { WorkspaceService } from '../../../../../workspace/workspace.service';
import { AppService } from '../../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  firstGridRowObjectFromPayload,
  hydratedGlobalParams,
} from '../../../../../shared/workspace-element-context';

import { EntityClassificationActionsService } from '../../services/entity-classification-actions.service';
import { 
  OverrideEntity,
  LegalEntity,
  Classification,
  TaxConsolidationGroup,
  RETURN_PREPARED_BY_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  YES_NO_OPTIONS
} from '../../models/entity-classification.models';
import { GtwSnackbarService } from 'projects/gtw-elements/src/app/shared/_services/gtw-snackbar.service';

@Component({
    selector: 'app-entity-classification-update',
    templateUrl: './entity-classification-update.component.html',
    styleUrls: ['./entity-classification-update.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityClassificationUpdateComponent implements OnInit, OnDestroy {
  private readonly subs = new Subscription();
  
  // Base URLs configuration
  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };
  
  // Header data
  header_data: any = {};
  globalService: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;
  GENERAL_CONFIG: any;
  
  // Row data for update mode
  rowData: OverrideEntity | null = null;
  
  // Filter parameters
  filterParams: any;
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  gridData: any;
  
  @Output() closeModal = new EventEmitter<void>();
  @Output() entityUpdated = new EventEmitter<OverrideEntity>();
  @Output() entityDeleted = new EventEmitter<string>();

  // Form and UI state
  entityForm: UntypedFormGroup;
  modalTitle = 'Update Override Entity';
  showEntityLoader = true;
  minimized = false;
  
  // Field states for update mode
  isReportingPeriodDisabled = false;
  
  // Loading states
  isSavingUpdate = false;
  isSavingDelete = false;
  
  // Data arrays
  allMasterEntities: LegalEntity[] = [];
  allClassifications: Classification[] = [];
  allTaxConsolidationGroups: TaxConsolidationGroup[] = [];
  
  // API completion flags
  entitiesApiCompleted = false;
  classificationsApiCompleted = false;
  taxGroupsApiCompleted = false;
  private isLoadingData = false;
  private overrideEntityBackup: OverrideEntity | null = null;
  
  // Constants for template
  readonly RETURN_PREPARED_BY_OPTIONS = RETURN_PREPARED_BY_OPTIONS;
  readonly ENTITY_TYPE_OPTIONS = ENTITY_TYPE_OPTIONS;
  readonly YES_NO_OPTIONS = YES_NO_OPTIONS;
  
  private destroy$ = new Subject<void>();

  constructor(
    private entityService: EntityClassificationActionsService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.entityForm = this.entityService.createEntityForm();
    // Legal entity, reporting period and tax year dates are never editable on the update screen
    this.entityService.disableFieldsForUpdateMode(this.entityForm);
    this.isReportingPeriodDisabled = true;
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.initializeService();
      this.loadInitialData();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.initializeService();
      this.loadInitialData();
    }));
    this.setupFormSubscriptions();
    this.initializeService();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      admin: gc.admin ?? '',
      api: gc.api ?? gc.base_url ?? '',
      custom: gc.custom ?? '',
      efile: gc.efile ?? '',
      pdfEngine: gc.pdfEngine ?? '',
      bulkPdfEngine: gc.bulkPdfEngine ?? '',
    };
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rowObj = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.rowData = rowObj && Object.keys(rowObj).length ? rowObj : null;
    this.gridData = this.rowData;
    this.populateFormWithRowData();
    this.cdr.markForCheck();
  }

  private getApiBaseUrl(): string {
    return this.baseURLs?.api || this.GENERAL_CONFIG?.base_url || '';
  }

  /**
   * Initialize service with base URLs and parameters
   */
  private initializeService(): void {
    if (this.baseURLs && this.globalService?.globalParams && this.filterParams) {
      console.log('Initializing service with:', {
        baseURLs: this.baseURLs,
        globalParams: this.globalService.globalParams,
        filterParams: this.filterParams
      });

      // Validate critical configuration
      if (!this.baseURLs?.api) {
        console.error('Service initialization warning: API base URL is missing');
      }
      
      if (!this.filterParams?.scenario) {
        console.error('Service initialization warning: Scenario is missing from filter params');
      }

      if (!this.globalService?.globalParams) {
        console.error('Service initialization warning: Global params is missing');
      }

      console.log('Service initialized successfully');
    }
  }

  /**
   * Set up form field subscriptions for reactive behavior
   */
  private setupFormSubscriptions(): void {
    // Watch tax consolidation group changes
    this.entityForm.get('TAX_CONSOLIDATION_GRP')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(taxGroup => {
        if (taxGroup) {
          this.entityService.handleTaxConsolidationGroupChange(
            this.entityForm, 
            taxGroup, 
            this.allTaxConsolidationGroups
          );
          this.cdr.markForCheck();
        }
      });

    // Watch classification changes
    this.entityForm.get('CLASSIFICATION')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(classification => {
        if (classification) {
          this.entityService.handleClassificationChange(
            this.entityForm, 
            classification, 
            this.allClassifications
          );
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    const apiBase = this.getApiBaseUrl();
    if (!apiBase || !this.globalService?.globalParams) {
      this.showEntityLoader = false;
      this.cdr.markForCheck();
      return;
    }

    if (this.isLoadingData) {
      return;
    }

    this.isLoadingData = true;
    this.entitiesApiCompleted = false;
    this.classificationsApiCompleted = false;
    this.taxGroupsApiCompleted = false;
    this.showEntityLoader = true;
    this.cdr.markForCheck();

    this.loadAllOverrideEntities();
    this.loadClassifications();
    this.loadTaxConsolidationGroups();
  }

  /**
   * Load all override entities
   */
  private loadAllOverrideEntities(): void {
    this.entityService.loadAllOverrideEntities(this.getApiBaseUrl(), this.globalService?.globalParams ?? {}, this.filterParams)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.checkLoadingCompletion())
      )
      .subscribe({
        next: (entities: LegalEntity[]) => {
          this.allMasterEntities = entities;
          this.entitiesApiCompleted = true;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading override entities:', error);
          this.entitiesApiCompleted = true;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Load classifications
   */
  private loadClassifications(): void {
    this.entityService.loadClassifications(this.getApiBaseUrl(), this.globalService?.globalParams ?? {}, this.filterParams)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.checkLoadingCompletion())
      )
      .subscribe({
        next: (classifications: Classification[]) => {
          this.allClassifications = classifications;
          this.classificationsApiCompleted = true;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading classifications:', error);
          this.classificationsApiCompleted = true;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Load tax consolidation groups
   */
  private loadTaxConsolidationGroups(): void {
    this.entityService.loadTaxConsolidationGroups(this.getApiBaseUrl(), this.globalService?.globalParams ?? {}, this.filterParams)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.checkLoadingCompletion())
      )
      .subscribe({
        next: (groups: TaxConsolidationGroup[]) => {
          this.allTaxConsolidationGroups = groups;
          this.taxGroupsApiCompleted = true;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading tax consolidation groups:', error);
          this.taxGroupsApiCompleted = true;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Check if all API calls are completed
   */
  private checkLoadingCompletion(): void {
    if (this.entitiesApiCompleted && this.classificationsApiCompleted && this.taxGroupsApiCompleted) {
      this.showEntityLoader = false;
      this.isLoadingData = false;
      this.populateFormWithRowData();
      this.cdr.markForCheck();
    }
  }

  /**
   * Populate form with row data for update mode
   */
  private populateFormWithRowData(): void {
    if (this.gridData && Object.keys(this.gridData).length > 0) {
      this.overrideEntityBackup = { ...this.gridData };
      this.entityService.populateFormWithEntity(this.entityForm, this.gridData);
      this.entityService.disableFieldsForUpdateMode(this.entityForm);
      this.isReportingPeriodDisabled = true;
      this.cdr.markForCheck();
    }
  }

  /**
   * Save entity (update or delete)
   */
  save(isDelete: boolean = false): void {
    if (!isDelete && !this.entityService.validateForm(this.entityForm)) {
      this.gtwSnackbarService.openSnackBar(
        'Please fill all the required fields',
        'gtw-snackbar--yellow'
      );
      this.cdr.markForCheck();
      return;
    }

    if (isDelete) {
      this.isSavingDelete = true;
    } else {
      this.isSavingUpdate = true;
    }
    this.cdr.markForCheck();

    const formValue = this.entityForm.getRawValue();
    formValue.SCENARIO = this.filterParams?.scenario ?? formValue.SCENARIO;

    const entity = this.entityService.prepareEntityForSave(
      formValue,
      this.allClassifications,
      this.allTaxConsolidationGroups
    );

    this.entityService.updateEntity(this.getApiBaseUrl(), entity, this.globalService?.globalParams ?? {}, this.filterParams, isDelete)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSavingUpdate = false;
          this.isSavingDelete = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response.callSuccess === '1' && response.errorMessage === 'null') {
            this.gtwSnackbarService.openSnackBar(
              'Override entity created successfully',
              'gtw-snackbar--green'
            );
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
            console.error('Update error:', response.errorMessage);
          }
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while saving. Please try again.',
            'gtw-snackbar--red'
          );
          console.error('Update error:', error);
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Delete entity (remove override)
   */
  delete(): void {
    if (!this.gridData) {
      return;
    }

    this.isSavingDelete = true;
    this.cdr.markForCheck();

    this.entityService.deleteEntity(
      this.getApiBaseUrl(),
      this.gridData.CHILD_ID,
      String(this.gridData.STUB),
      this.gridData,
      this.globalService?.globalParams ?? {},
      this.filterParams
    )
    .pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isSavingDelete = false;
        this.cdr.markForCheck();
      })
    )
    .subscribe({
      next: (response: any) => {
        if (response.callSuccess === '1' && response.errorMessage === 'null') {
          this.gtwSnackbarService.openSnackBar('Override entity deleted successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
          console.error('Delete error:', response.errorMessage);
        }
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while deleting. Please try again.',
          'gtw-snackbar--red'
        );
        console.error('Delete error:', error);
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Reset form
   */
  reset(): void {
    if (this.overrideEntityBackup) {
      this.entityService.populateFormWithEntity(this.entityForm, this.overrideEntityBackup);
      this.entityService.disableFieldsForUpdateMode(this.entityForm);
      this.cdr.markForCheck();
    }
  }

  /**
   * Cancel and close modal
   */
  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-entity-classification-update-ce')) {
      document.querySelector('gtw-entity-classification-update-ce')?.dispatchEvent(updateEvent);
    }
  }

  /**
   * Toggle modal minimized state
   */
  toggleMinimized(): void {
    this.minimized = !this.minimized;
    this.cdr.markForCheck();
  }

  /**
   * Get field error message
   */
  getFieldError(fieldName: string): string | null {
    return this.entityService.getFieldError(this.entityForm, fieldName);
  }

  /**
   * Check if field has error
   */
  hasFieldError(fieldName: string): boolean {
    return this.entityService.hasFieldError(this.entityForm, fieldName);
  }

  /**
   * Get available reporting periods
   */
  get reportingPeriods() {
    return this.entityService.getReportingPeriods();
  }

  /**
   * Track by function for ngFor performance
   */
  trackByIndex(index: number, item: any): number {
    return this.entityService.trackByIndex(index, item);
  }

  /**
   * Track by entity ID for ngFor performance
   */
  trackByEntityId(index: number, entity: LegalEntity): string {
    return this.entityService.trackByEntityId(index, entity);
  }
}